import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import SectionHeading from "@/components/SectionHeading";
import { MetricListItem, MetricListItemGrid } from "@/components/ListItem";
import Banner from "@/components/Banner";
import Tag from "@/components/Tag";
import ErrorBoundary from "@/components/ErrorBoundary";
import EmptyState from "@/components/EmptyState";
import Table, { TableHeading } from "@/components/Table";
import { buildTagColumn } from "@/components/Table/buildTagColumn";
import { Path } from "@/router";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import {
  GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY,
  GET_MEMBERSHIP_DETAILS_QUERY,
  GET_PROSPECT_PROGRAM_QUERY,
  GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY
} from "../../person/queries";
import { ProgramStatus, ProgramTypeName } from "@/types";
import type { ExperienceCreditSource, ExperienceCreditSuspensionPeriod, ExperienceCredits, Person } from "@/types";
import { toUTCFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import {
  buildLevelMetrics,
  buildLevelDateMetrics,
  buildProgramSummarySectionData,
  getLastActivePgmLevelNumeral,
  hasStartedProspectProgram,
  programSummaryStatusTagType,
  shouldShowAcceptableProgressTerminationBanner,
  LEVEL_NUMBERS
} from "@/features/person/utils/pgm-levels";
import { buildPATMetric } from "@/features/person/utils/pat-metric";
import {
  PAT_ACTIVITY_SCROLL_ID,
  QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
  isMembershipProgressScrollTarget
} from "@/features/person/utils/membershipProgressLinks";
import { isPatCredentialStillMetForCurrentPath } from "@/features/person/utils/pathAwareExpiration";
import { buildExperienceCreditsMetric } from "@/features/person/utils/experience-credits-metric";
import {
  buildLevelRequirements,
  omitSyntheticMemberEnrollmentOverlay,
  requirementRowHasDestination,
  requirementRowScrollsInPage,
  RequirementRow
} from "@/features/person/utils/pgm-requirements";
import {
  membershipApplicationRequirementHref,
  withMembershipApplicationLinks
} from "@/features/person/utils/membershipApplicationLinks";
import {
  buildQualifyingLevelRequirements,
  withAffiliatePathAssociateApplication
} from "@/features/person/utils/qualifyingLevelRequirements";
import {
  getActiveSuspensionFromProgress,
  getSuspensionBannerConfig,
  formatTerminationDeadline,
  getGptpProgressGuidance,
  getLevelNumeralAtSuspensionStart,
  ACCEPTABLE_PROGRESS_TERMINATION_BANNER_DESCRIPTION,
  getAcceptableProgressTerminationGuidance
} from "@/features/person/utils/pgm-suspensions";
import { buildQualifyingLevelMetric } from "@/features/person/utils/qualifyingLevelMetric";
import {
  AT_A_GLANCE_TABLE_TEST_ID,
  AT_A_GLANCE_TABLE_WRAP_TEST_ID,
  type AtAGlanceRow,
  atAGlanceRowHasDestination,
  atAGlanceRowScrollsInPage,
  resolveAtAGlanceHref,
  toAtAGlanceRows
} from "@/features/person/utils/atAGlanceRows";
import PATActivityTable from "@/features/person/components/PATActivityTable";
import { withBackgroundCheckRequirementLinks } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckRequirementLinks";
import { canViewBackgroundCheckStaffSection } from "@/features/person/components/BackgroundCheckOrders/backgroundCheckEligibility";
import { useFlags } from "@/hooks/useFeatureFlags";
import { DOMAIN, PERMISSION, useCanViewPartialBackgroundCheck, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { usePersonMembershipAccess } from "../../person/_usePersonMembershipAccess";

const PATH_TO_MEMBERSHIP_PRIVACY_BANNER_DESCRIPTION =
  "To maintain privacy, please limit the information you share with employers to high-level updates only.";
const PATH_TO_MEMBERSHIP_PRIVACY_BANNER_SUBTEXT =
  "If an employer requests more specific information, please direct them to speak with the Associate.";
const REQUIREMENT_NAME_CLASS = "min-w-0 max-w-full whitespace-normal break-words";
const REQUIREMENT_LINK_CLASS = `${REQUIREMENT_NAME_CLASS} underline underline-offset-2`;
const EXPERIENCE_CREDITS_SCROLL_ID = "experience-credits-section";
const DETAILED_PROGRESS_HEADING = "Detailed Progress";
const DETAIL_TABLE_HEADING_PROPS = {
  headingSize: "small",
  topSpacing: "compact"
} as const;

const QUALIFYING_LEVEL_REQUIREMENTS_INTRO = "View all requirements and completion status for the Qualifying Level.";
const QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY =
  "View all requirements and completion status for the Qualifying Level below. Prospects applying to the Associate Program for Level 1 must have eligible employment, which is tracked as part of their application. Those applying to the Affiliate program can postpone eligible employment until Level 2.";
const LEVEL_1_AFFILIATE_EMPLOYMENT_COPY =
  "Affiliates must have eligible employment to apply for the Associate program/Level 2.";

function appendHeadingBody(intro: string, extra?: string): string {
  return extra ? `${intro} ${extra}` : intro;
}

function isAffiliate(person: Person): boolean {
  return person.latestProgram?.programType?.name === ProgramTypeName.Affiliate;
}

function isProspectOrNoProgram(person: Person): boolean {
  const name = person.latestProgram?.programType?.name;
  return name == null || name === ProgramTypeName.Prospect;
}

function qualifyingLevelRequirementsBody(person: Person): string {
  return isProspectOrNoProgram(person)
    ? QUALIFYING_LEVEL_PROSPECT_REQUIREMENTS_BODY
    : QUALIFYING_LEVEL_REQUIREMENTS_INTRO;
}

function levelRequirementsBody(person: Person, n: number): string {
  const extra = n === 1 && isAffiliate(person) ? LEVEL_1_AFFILIATE_EMPLOYMENT_COPY : undefined;
  return appendHeadingBody(`View all requirements and completion status for Level ${n} below.`, extra);
}

type PersonData = { person: Person };
type SectionExpandRequest = { sectionId: string; token: number };

interface ExperienceCreditRow extends ExperienceCreditSource {
  id: string;
  overlappingSuspensions: ExperienceCreditSuspension[];
}

interface ExperienceCreditSuspension {
  id: string;
  text: string;
}

function formatExperienceCreditType(source: ExperienceCreditSource): string {
  switch (source.creditType?.toUpperCase()) {
    case "CO2":
      return "Associate's Degree";
    case "CO4":
      return "Bachelor's Degree";
    case "PGM":
      return "Student Internship";
    case "EMPLOYMENT":
      return "Employment";
    case "PRE_ASSOCIATE_EMPLOYMENT":
      return "Pre-Associate Employment";
    default:
      return source.origin === "education" ? "Academic Degree" : "--";
  }
}

function formatCreditDate(date: string | null): string {
  return date ? (toUTCFormat(date) ?? "--") : "--";
}

function getExperienceCreditSortDate(source: ExperienceCreditSource): number {
  const date = source.endedOn ?? source.startedOn;
  return date ? Date.parse(date) : 0;
}

function parseCreditDate(date: string | null): number | null {
  if (!date) return null;

  const timestamp = Date.parse(date);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function hasDateRangeOverlap(
  source: ExperienceCreditSource,
  suspensionPeriod: ExperienceCreditSuspensionPeriod
): boolean {
  const sourceStart = parseCreditDate(source.startedOn ?? source.endedOn);
  const sourceEnd = parseCreditDate(source.endedOn ?? source.startedOn);
  const suspensionStart = parseCreditDate(suspensionPeriod.startedOn ?? suspensionPeriod.endedOn);
  const suspensionEnd = parseCreditDate(suspensionPeriod.endedOn ?? suspensionPeriod.startedOn);

  if (sourceStart === null || sourceEnd === null || suspensionStart === null || suspensionEnd === null) {
    return false;
  }

  return sourceStart <= suspensionEnd && suspensionStart <= sourceEnd;
}

function formatSuspensionWindow(suspensionPeriod: ExperienceCreditSuspensionPeriod): string {
  const label = suspensionPeriod.code || "Suspension";

  return `${label}: ${formatCreditDate(suspensionPeriod.startedOn)} - ${formatCreditDate(suspensionPeriod.endedOn)}`;
}

function buildOverlappingSuspensions(
  source: ExperienceCreditSource,
  suspensionPeriods: ExperienceCreditSuspensionPeriod[]
): ExperienceCreditSuspension[] {
  const overlappingSuspensions = suspensionPeriods.filter((period) => hasDateRangeOverlap(source, period));

  return overlappingSuspensions.map((suspensionPeriod, index) => ({
    id: `${suspensionPeriod.code}-${suspensionPeriod.startedOn}-${suspensionPeriod.endedOn}-${index}`,
    text: formatSuspensionWindow(suspensionPeriod)
  }));
}

function buildExperienceCreditRows(experienceCredits?: ExperienceCredits | null): ExperienceCreditRow[] {
  const suspensionPeriods = experienceCredits?.excludedSuspensionPeriods ?? [];

  return (
    experienceCredits?.sources
      .map((source, index) => ({
        ...source,
        id: `${source.origin}-${source.sourceId ?? source.creditType ?? index}`,
        overlappingSuspensions: buildOverlappingSuspensions(source, suspensionPeriods)
      }))
      .sort((first, second) => getExperienceCreditSortDate(second) - getExperienceCreditSortDate(first)) ?? []
  );
}

export default function PathToMembership() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonMembership } = usePersonMembershipAccess();
  const [searchParams] = useSearchParams();
  const flags = useFlags();
  const canViewBackgroundCheck = useCanViewPartialBackgroundCheck();
  const staffSectionVisible = canViewBackgroundCheckStaffSection({
    flags,
    canViewPartialBackgroundCheck: canViewBackgroundCheck
  });
  const canManageApplications = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  const canViewPersonEducation = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonEducation
  });
  const loadMembershipApplications = canManageApplications;
  const [sectionExpandRequest, setSectionExpandRequest] = useState<SectionExpandRequest | null>(null);

  const requestSectionExpand = (sectionId: string) => {
    setSectionExpandRequest({ sectionId, token: Date.now() });
  };

  useEffect(() => {
    if (!sectionExpandRequest) return;
    const frame = requestAnimationFrame(() => scrollToElement(sectionExpandRequest.sectionId));
    return () => cancelAnimationFrame(frame);
  }, [sectionExpandRequest]);

  const skipMembershipQueries = !loaded || !canViewPersonMembership || !pgaId;

  const { data, error, loading } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const {
    data: educationData,
    error: educationError,
    loading: educationLoading
  } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_EDUCATION_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries || !canViewPersonEducation
  });

  const {
    data: prospectData,
    error: prospectError,
    loading: prospectLoading
  } = useQuery<PersonData>(GET_PROSPECT_PROGRAM_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries
  });

  const {
    data: applicationsData,
    error: applicationsError,
    loading: applicationsLoading
  } = useQuery<PersonData>(GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: skipMembershipQueries || !loadMembershipApplications
  });

  useEffect(() => {
    if (loading || prospectLoading) return;

    const scrollTo = searchParams.get("scrollTo");
    if (!scrollTo || !isMembershipProgressScrollTarget(scrollTo)) return;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("scrollTo");
    window.history.replaceState({}, "", currentUrl.toString());

    setSectionExpandRequest({ sectionId: scrollTo, token: Date.now() });
  }, [loading, prospectLoading, searchParams]);

  if (!loaded || !canViewPersonMembership) {
    return null;
  }

  if (loading || prospectLoading || (canViewPersonEducation && educationLoading)) {
    return (
      <div className="pt-12">
        <Table data={[]} columns={atAGlanceColumns} loading data-testid={AT_A_GLANCE_TABLE_TEST_ID} />
      </div>
    );
  }

  const combinedError = error || prospectError;
  if (combinedError) {
    const errorMessage = combinedError.graphQLErrors?.map((e) => e.message).join(", ") || combinedError.message;
    return (
      <ErrorBoundary
        className="mt-12"
        variant="inline"
        title="Error loading path to membership data"
        message={errorMessage}
      />
    );
  }

  const membershipPerson = {
    ...(data?.person || ({} as Person)),
    ...(!educationError && educationData?.person ? educationData.person : {})
  };
  const membershipApplications = applicationsData?.person?.membershipApplications;
  const person: Person = {
    ...membershipPerson,
    prospectProgram: prospectData?.person?.prospectProgram ?? membershipPerson.prospectProgram,
    accountCreatedAt: prospectData?.person?.accountCreatedAt ?? membershipPerson.accountCreatedAt,
    ...(membershipApplications ? { membershipApplications } : {})
  };
  const canLinkApplications = canManageApplications && !applicationsLoading && !applicationsError;
  const withApplicationLinks = (rows: RequirementRow[]) =>
    canLinkApplications ? withMembershipApplicationLinks(rows, { ...person, membershipApplications }) : rows;
  const withLevelRequirementRows = (rows: RequirementRow[], levelNumber: number) =>
    omitSyntheticMemberEnrollmentOverlay(withApplicationLinks(rows), person, levelNumber, {
      applicationsResolved: !canManageApplications || canLinkApplications
    });
  const educationProgressFailed = !!educationError;
  const activeSuspension = person.pgmProgressStatus ? getActiveSuspensionFromProgress(person.pgmProgressStatus) : null;
  const suspensionBannerConfig = activeSuspension ? getSuspensionBannerConfig(activeSuspension.code) : null;
  const terminationDeadline = activeSuspension ? formatTerminationDeadline(activeSuspension) : null;
  const suspensionLevelNumeral = activeSuspension
    ? getLevelNumeralAtSuspensionStart(person.pgmProgressStatus, activeSuspension)
    : null;
  const progressGuidance = activeSuspension
    ? getGptpProgressGuidance(activeSuspension.code, suspensionLevelNumeral)
    : null;
  const suspensionSubText = [progressGuidance, terminationDeadline].filter((line): line is string => Boolean(line));
  const isSuspended = !!activeSuspension && person.latestProgram?.status !== ProgramStatus.TERMINATED;
  const patData = prospectData?.person?.prospectProgram?.playingAbilityTestStatus;
  const levelMetrics = educationProgressFailed
    ? []
    : buildLevelMetrics(person, isSuspended).map((metric, index) => {
        const sectionId = `level-${LEVEL_NUMBERS[index]}-requirements-table`;
        return {
          ...metric,
          scrollToId: sectionId,
          onBeforeScroll: () => requestSectionExpand(sectionId)
        };
      });
  const programSummary = buildProgramSummarySectionData(person);
  const hasProgramSummaryInfo = programSummary.metrics.some((metric) => Boolean(metric.value));
  const lastActiveLevelNumeral = getLastActivePgmLevelNumeral(person);
  const showAcceptableProgressTerminationBanner = shouldShowAcceptableProgressTerminationBanner(person);
  const patMustBeRetaken = !!patData?.expired && !isPatCredentialStillMetForCurrentPath(person, patData);
  const experienceCreditsMetric = {
    ...buildExperienceCreditsMetric(person.experienceCredits, {
      scrollToId: EXPERIENCE_CREDITS_SCROLL_ID,
      programStarted: hasStartedProspectProgram(person)
    }),
    onBeforeScroll: () => requestSectionExpand(EXPERIENCE_CREDITS_SCROLL_ID)
  };
  const patMetric = {
    ...buildPATMetric(patData, {
      scrollToId: PAT_ACTIVITY_SCROLL_ID,
      pathAwareExpiration: true,
      person
    }),
    onBeforeScroll: () => requestSectionExpand(PAT_ACTIVITY_SCROLL_ID)
  };
  const qualifyingLevelRequirements = withBackgroundCheckRequirementLinks(buildQualifyingLevelRequirements(person), {
    staffSectionVisible,
    pgaId: pgaId ?? ""
  });
  const metrics = [
    patMetric,
    {
      ...buildQualifyingLevelMetric(person, isSuspended, qualifyingLevelRequirements),
      scrollToId: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
      onBeforeScroll: () => requestSectionExpand(QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID)
    },
    ...levelMetrics,
    experienceCreditsMetric
  ];

  const qualifyingLevelSection: RequirementsSectionProps = {
    sectionId: "qualifying-requirements-table",
    heading: "Qualifying Level Requirements",
    body: qualifyingLevelRequirementsBody(person),
    dateMetrics: buildLevelDateMetrics(person, 0, {
      isSuspended,
      suspensionReason: suspensionBannerConfig?.description,
      terminationTimeRemaining: activeSuspension?.timeRemaining
    }),
    dateMetricsTestId: "qualifying-date-metrics",
    requirements: withApplicationLinks(qualifyingLevelRequirements),
    tableTestId: "qualifying-requirements-table",
    emptyTitle: "No Qualifying Level Requirements",
    emptyCaption: "Qualifying level requirements are not available for this person"
  };

  const levelRequirementSections: RequirementsSectionProps[] = educationProgressFailed
    ? []
    : LEVEL_NUMBERS.map((n) => ({
        sectionId: `level-${n}-requirements-table`,
        heading: `Level ${n} Requirements`,
        body: levelRequirementsBody(person, n),
        dateMetrics: buildLevelDateMetrics(person, n, {
          isSuspended,
          suspensionReason: suspensionBannerConfig?.description,
          terminationTimeRemaining: activeSuspension?.timeRemaining
        }),
        dateMetricsTestId: `level-${n}-date-metrics`,
        requirements: (() => {
          const rows = buildLevelRequirements(person, n);
          const withAssociateApplication = n === 1 ? withAffiliatePathAssociateApplication(rows, person) : rows;
          return withLevelRequirementRows(withAssociateApplication, n);
        })(),
        tableTestId: `level-${n}-requirements-table`,
        emptyTitle: `No Level ${n} Requirements`,
        emptyCaption: `Level ${n} requirements are not available for this person`
      }));

  const requirementsSections: RequirementsSectionProps[] = educationProgressFailed
    ? [qualifyingLevelSection]
    : [qualifyingLevelSection, ...levelRequirementSections];

  return (
    <>
      {isSuspended && suspensionBannerConfig && (
        <Banner
          variant="warn"
          description={suspensionBannerConfig.description}
          subText={suspensionSubText.length > 0 ? suspensionSubText : undefined}
          testId="suspension-banner"
          primaryAction={{
            label: "View Suspension History",
            link: `/people/${pgaId}/membership/overview?scrollTo=suspension-history-table`,
            isInternal: true
          }}
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {showAcceptableProgressTerminationBanner && (
        <Banner
          variant="error"
          description={ACCEPTABLE_PROGRESS_TERMINATION_BANNER_DESCRIPTION}
          subText={getAcceptableProgressTerminationGuidance(lastActiveLevelNumeral, patMustBeRetaken)}
          testId="acceptable-progress-termination-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {applicationsError && (
        <Banner
          variant="warn"
          description="Membership application links could not be loaded."
          subText="You can still review Path to Membership."
          testId="membership-applications-link-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {educationError && (
        <Banner
          variant="warn"
          description="Education progress could not be loaded."
          subText="You can still review Path to Membership."
          testId="membership-education-progress-banner"
          hideSecondaryDismiss
          className="mt-8"
          inline
        />
      )}
      {programSummary.heading !== "Path to Membership" ? (
        <>
          <SectionHeading
            heading={programSummary.heading}
            tag={
              programSummary.status ? (
                <Tag
                  tagType={programSummaryStatusTagType(programSummary.status)}
                  tagStyle="strong"
                  size="small"
                  testId="program-summary-status"
                >
                  {programSummary.status}
                </Tag>
              ) : undefined
            }
          />
          {hasProgramSummaryInfo && <ProgramSummaryMetrics metrics={programSummary.metrics} />}
        </>
      ) : null}
      <div
        className={programSummary.heading !== "Path to Membership" ? "mt-6" : "pt-12"}
        data-testid={AT_A_GLANCE_TABLE_WRAP_TEST_ID}
      >
        <Table
          data={toAtAGlanceRows(metrics)}
          columns={atAGlanceColumns}
          loading={false}
          data-testid={AT_A_GLANCE_TABLE_TEST_ID}
          getRowLink={(row) => {
            const href = resolveAtAGlanceHref(row.original);
            return href ? { to: href } : null;
          }}
          getRowClasses={(row) => (atAGlanceRowHasDestination(row.original) ? "" : "cursor-default")}
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Path to Membership Requirements"
              caption="Progress details are not available for this person"
            />
          )}
        />
      </div>
      <SectionHeading heading={DETAILED_PROGRESS_HEADING} />
      <PrivacyBanner />
      <div id={PAT_ACTIVITY_SCROLL_ID}>
        <PATActivityTable
          patStatus={patData}
          loading={false}
          enableNavigation={true}
          pgaId={pgaId}
          personId={person.id}
          person={person}
          pathAwareExpiration
          headingSize={DETAIL_TABLE_HEADING_PROPS.headingSize}
          topSpacing={DETAIL_TABLE_HEADING_PROPS.topSpacing}
        />
      </div>
      {requirementsSections.map((section) => (
        <RequirementsSection key={section.sectionId} {...section} />
      ))}
      <ExperienceCreditsSection experienceCredits={person.experienceCredits} />
    </>
  );
}

function PrivacyBanner() {
  return (
    <Banner
      variant="info"
      description={PATH_TO_MEMBERSHIP_PRIVACY_BANNER_DESCRIPTION}
      subText={PATH_TO_MEMBERSHIP_PRIVACY_BANNER_SUBTEXT}
      hideSecondaryDismiss
      testId="path-to-membership-privacy-banner"
      inline
    />
  );
}

function toMetricListItemProps(metric: MetricCardType<Path>) {
  return {
    iconName: metric.iconName ?? "info",
    overline: metric.label,
    headline: metric.value ?? "--",
    tooltipTitle: typeof metric.tooltip?.title === "string" ? metric.tooltip.title : undefined,
    tooltipSupportingText:
      typeof metric.tooltip?.supportingText === "string" ? metric.tooltip.supportingText : undefined
  };
}

function ProgramSummaryMetrics({ metrics }: { metrics: MetricCardType<Path>[] }) {
  if (metrics.length === 0) return null;

  return (
    <MetricListItemGrid className="-mt-2" testId="program-summary-metrics">
      {metrics.map((metric) => (
        <MetricListItem key={metric.label} {...toMetricListItemProps(metric)} />
      ))}
    </MetricListItemGrid>
  );
}

const experienceCreditColumns: ColumnDef<ExperienceCreditRow, unknown>[] = [
  {
    accessorKey: "description",
    header: "Description",
    cell: ({ row }) => <span>{row.original.description || "--"}</span>,
    enableSorting: false,
    size: 220
  },
  {
    accessorKey: "creditType",
    header: "Type",
    cell: ({ row }) => <span>{formatExperienceCreditType(row.original)}</span>,
    enableSorting: false,
    size: 180
  },
  {
    accessorKey: "credits",
    header: "Credits",
    enableSorting: false,
    size: 100
  },
  {
    accessorKey: "startedOn",
    header: "Credit Start Date",
    cell: ({ row }) => <span>{formatCreditDate(row.original.startedOn)}</span>,
    enableSorting: false,
    size: 140
  },
  {
    accessorKey: "endedOn",
    header: "Credit End Date",
    cell: ({ row }) => <span>{formatCreditDate(row.original.endedOn)}</span>,
    enableSorting: false,
    size: 140
  },
  {
    accessorKey: "overlappingSuspensions",
    header: "Adjustments",
    cell: ({ row }) => {
      const suspensions = row.original.overlappingSuspensions;

      if (!suspensions.length) {
        return <span>--</span>;
      }

      return (
        <div className="flex flex-col gap-1 whitespace-normal">
          {suspensions.map((suspension) => (
            <span key={suspension.id}>{suspension.text}</span>
          ))}
        </div>
      );
    },
    enableSorting: false,
    size: 240,
    minSize: 240,
    meta: {
      className: "max-lg:flex-col max-lg:items-start max-lg:gap-1"
    }
  }
];

function RequirementName({
  label,
  hasDestination,
  onInPageNavigate
}: {
  label: string;
  hasDestination: boolean;
  onInPageNavigate?: () => void;
}) {
  if (onInPageNavigate) {
    return (
      <button
        type="button"
        className={`bg-transparent p-0 border-0 text-inherit ${REQUIREMENT_LINK_CLASS} cursor-pointer text-left`}
        onClick={onInPageNavigate}
      >
        {label}
      </button>
    );
  }

  return <span className={hasDestination ? REQUIREMENT_LINK_CLASS : REQUIREMENT_NAME_CLASS}>{label}</span>;
}

const atAGlanceColumns: ColumnDef<AtAGlanceRow, unknown>[] = [
  {
    accessorKey: "requirement",
    header: "Requirement",
    cell: ({ row }) => (
      <RequirementName
        label={row.original.requirement}
        hasDestination={atAGlanceRowHasDestination(row.original)}
        onInPageNavigate={
          atAGlanceRowScrollsInPage(row.original) ? () => handleAtAGlanceRowClick(row.original) : undefined
        }
      />
    ),
    enableSorting: false,
    size: 220,
    meta: { headerClassName: "hide-header-in-column", className: "whitespace-normal" }
  },
  buildTagColumn<AtAGlanceRow>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => row.status,
    getTagType: (row) => row.statusType,
    getIconName: (row) => (row.status === "Not Started" ? "lock" : undefined),
    getTooltip: (row) => row.tooltip,
    size: 140
  }),
  {
    accessorKey: "details",
    header: "Details",
    cell: ({ row }) => (
      <span className={row.original.details ? "whitespace-normal" : "opacity-20"}>{row.original.details || "--"}</span>
    ),
    enableSorting: false,
    size: 360,
    meta: { headerClassName: "hide-header-in-column", className: "whitespace-normal" }
  }
];

function handleAtAGlanceRowClick(row: AtAGlanceRow) {
  if (!atAGlanceRowHasDestination(row)) return;
  if (row.onClick) {
    row.onClick();
    return;
  }
  if (row.onBeforeScroll) {
    row.onBeforeScroll();
    return;
  }
  if (row.scrollToId) {
    scrollToElement(row.scrollToId);
  }
}

const requirementsColumns = [
  {
    accessorKey: "name",
    header: "Requirement",
    cell: ({ row }: { row: { original: RequirementRow } }) => (
      <RequirementName
        label={row.original.name}
        hasDestination={requirementRowHasDestination(row.original)}
        onInPageNavigate={
          requirementRowScrollsInPage(row.original)
            ? () => {
                const sectionId = row.original.scrollToSectionId;
                if (sectionId) scrollToElement(sectionId);
              }
            : undefined
        }
      />
    ),
    enableSorting: false,
    size: 300,
    meta: { headerClassName: "hide-header-in-column", className: "whitespace-normal" }
  },
  {
    accessorKey: "completionDate",
    header: "Completion Date",
    cell: ({ row }: { row: { original: RequirementRow } }) => <span>{row.original.completionDate || "--"}</span>,
    enableSorting: false,
    size: 150
  },
  buildTagColumn<RequirementRow>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => row.status,
    getTagType: (row) => row.statusType,
    getIconName: (row) => (row.status === "Locked" ? "lock" : undefined),
    getTooltipText: (row) => (row.status === "Locked" ? "Previous Requirement(s) Must Be Met First" : undefined),
    getTooltip: (row) => row.statusTooltip,
    size: 120
  })
];

function ExperienceCreditsSection({ experienceCredits }: { experienceCredits?: ExperienceCredits | null }) {
  const rows = buildExperienceCreditRows(experienceCredits);
  const totalCreditsHeadline = experienceCredits
    ? experienceCredits.threshold != null
      ? `${experienceCredits.total} of ${experienceCredits.threshold}`
      : String(experienceCredits.total)
    : "--";
  const sectionBody =
    experienceCredits?.threshold != null
      ? `View all activities for this person that qualify as experience credits toward their eligibility for membership. A minimum of ${experienceCredits.threshold} credits is required to meet the membership application requirement.`
      : "View all activities for this person that qualify as experience credits toward their eligibility for membership.";

  return (
    <div id={EXPERIENCE_CREDITS_SCROLL_ID}>
      <TableHeading heading="Experience Credits" body={sectionBody} {...DETAIL_TABLE_HEADING_PROPS} />
      <MetricListItemGrid className="-mt-2" testId="experience-credits-metrics">
        <MetricListItem iconName="collections_bookmark" overline="Total Credits" headline={totalCreditsHeadline} />
        <MetricListItem
          iconName="school"
          overline="Education Credits"
          headline={experienceCredits ? String(experienceCredits.educationCredits) : "--"}
        />
        <MetricListItem
          iconName="business_center"
          overline="Employment Credits"
          headline={experienceCredits ? String(experienceCredits.employmentCredits) : "--"}
        />
      </MetricListItemGrid>
      <div className="mt-6">
        <Table
          data={rows}
          columns={experienceCreditColumns}
          loading={false}
          data-testid="experience-credits-table"
          renderNoDataEmptyState={() => (
            <EmptyState
              title="No Experience Credits"
              caption="No experience credits have been recorded yet"
              iconName="collections_bookmark"
            />
          )}
        />
      </div>
    </div>
  );
}

interface RequirementsSectionProps {
  sectionId: string;
  heading: string;
  body?: string;
  dateMetrics: MetricCardType<Path>[];
  dateMetricsTestId: string;
  requirements: RequirementRow[];
  tableTestId: string;
  emptyTitle: string;
  emptyCaption: string;
}

function RequirementsSection({
  sectionId,
  heading,
  body,
  dateMetrics,
  dateMetricsTestId,
  requirements,
  tableTestId,
  emptyTitle,
  emptyCaption
}: RequirementsSectionProps) {
  return (
    <>
      <div id={sectionId}>
        <TableHeading heading={heading} body={body} {...DETAIL_TABLE_HEADING_PROPS} />
      </div>
      <MetricListItemGrid className="-mt-2" testId={dateMetricsTestId}>
        {dateMetrics.map((metric) => (
          <MetricListItem key={metric.label} {...toMetricListItemProps(metric)} />
        ))}
      </MetricListItemGrid>
      <div className="mt-6">
        <Table
          data={requirements}
          columns={requirementsColumns}
          loading={false}
          data-testid={tableTestId}
          getRowLink={(row) => {
            const href = membershipApplicationRequirementHref(row.original);
            if (href) return { to: href as Path };
            if (row.original.detailPath) {
              return { to: row.original.detailPath };
            }
            return null;
          }}
          getRowClasses={(row) => (requirementRowHasDestination(row.original) ? "" : "cursor-default")}
          renderNoDataEmptyState={() => <EmptyState title={emptyTitle} caption={emptyCaption} iconName="school" />}
        />
      </div>
    </>
  );
}
